import { Store } from '@ngrx/store';
import { LocalStorageService } from '@rtr-factory/utils/services';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import {
  ConfigService,
  KeycloakRole,
  Membership,
  Organization,
  OrganizationsService,
  UserService,
  organizationsActions,
  routerActions,
  userActions,
} from 'rio-core';
import { TrackingService } from 'rio-tracking';
import { Observable, forkJoin, from, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

export function rioUserInitializer(
  keycloak: Keycloak,
  store: Store,
  organizationService: OrganizationsService,
  userService: UserService,
  trackingService: TrackingService,
  configService: ConfigService,
  localStorageService: LocalStorageService,
): () => Observable<unknown> {
  return () =>
    from(
      keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
      }),
    ).pipe(
      catchError((error: unknown) => of(error)),
      switchMap(() => keycloak.loadUserProfile()),
      switchMap((user: KeycloakProfile) =>
        forkJoin([of(user), organizationService.getOrganizations(), userService.getMemberships(user.id)]),
      ),
      tap(([user, organizations, memberships]: [KeycloakProfile, Organization[], Membership[]]) => {
        const mixpanelKey = configService.config.mixpanelKey;
        const mixpanelHostUrl = configService.config.mixpanelHostUrl;
        const keycloakRole = getKeycloakRole(keycloak);
        const organizationId = getSelectedOrganizationId(memberships, organizations, localStorageService);

        setTracking(trackingService, mixpanelKey, mixpanelHostUrl, user, organizationId, organizations);
        setUser(store, user);
        setKeycloakRole(store, keycloakRole);
        setMemberships(store, memberships);
        setOrganizations(store, organizations, organizationId);

        navigate(store, keycloak, organizationId, keycloakRole);
      }),
    );
}

function getSelectedOrganizationId(
  memberships: Membership[],
  organizations: Organization[],
  localStorageService: LocalStorageService,
): string | null {
  const selectedOrganizationId = localStorageService.getValue('lastUsedOrganizationId');

  if (selectedOrganizationId) {
    const isOrganizationAvailable = organizations.some(organization => organization.id === selectedOrganizationId);
    if (isOrganizationAvailable) {
      return selectedOrganizationId;
    }
  }

  return memberships.find(membership => Boolean(membership.organizationId))?.organizationId ?? null;
}

function getKeycloakRole(keycloak: Keycloak): KeycloakRole {
  if (keycloak.hasRealmRole('super_admin')) {
    return 'super_admin';
  }

  if (keycloak.hasRealmRole('support')) {
    return 'support';
  }
}

function setTracking(
  trackingService: TrackingService,
  mixpanelKey: string,
  mixpanelHostUrl: string,
  user: KeycloakProfile,
  organizationId: string,
  organizations: Organization[],
) {
  trackingService.init(mixpanelKey, mixpanelHostUrl);
  trackingService.identify(user.id);

  const isInternal = organizations.find(org => org.id === organizationId)?.isInternal;

  trackingService.setOrganization(organizationId, isInternal);
}

function setUser(store: Store, user: KeycloakProfile) {
  store.dispatch(
    userActions.loadUser({
      user: {
        id: user.id,
        email: user.email,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
      },
    }),
  );
}

function setKeycloakRole(store: Store, keycloakRole: KeycloakRole) {
  if (keycloakRole) {
    store.dispatch(userActions.setKeycloakRole({ keycloakRole }));
  }
}

function setMemberships(store: Store, memberships: Membership[]) {
  store.dispatch(userActions.setMemberships({ memberships }));
}

function setOrganizations(store: Store, organizations: Organization[], selectedOrganizationId: string | null) {
  store.dispatch(organizationsActions.loadOrganizationsSuccess({ organizations }));

  if (organizations?.length === 0) {
    return;
  }

  const organizationId = organizations.find(org => org.id === selectedOrganizationId)?.id;

  if (organizationId) {
    store.dispatch(userActions.selectOrganization({ organizationId }));
  } else {
    store.dispatch(userActions.selectOrganization({ organizationId: organizations[0].id }));
  }
}

function navigate(
  store: Store,
  keycloak: Keycloak,
  selectedOrganizationId: string,
  keycloakRole: 'super_admin' | 'support',
) {
  if (!selectedOrganizationId && !keycloakRole) {
    store.dispatch(routerActions.navigateToNoOrganizationPage());
  } else {
    // acr indicates if the user logs into application
    // 0 === refresh of active session
    // 1 === login into application
    const hasUserLoggedIn = keycloak.idTokenParsed.acr === '1';
    if (hasUserLoggedIn) {
      store.dispatch(routerActions.initialNavigationToDefaultRoute());
    }
  }
}
